import { Link, useLocation } from "wouter";
import { mobileBottomNavItems, getActiveSection } from "@/lib/navigationConfig";
import { cn } from "@/lib/utils";
import { getCrossDomainHref, isCapitalDomain, needsCrossDomainNav } from "@/lib/domainUtils";
import { SIDE_EVENTS_HOST } from "@shared/sideEventsHub";

export function MobileBottomNav() {
  const [location] = useLocation();

  // Capital has its own mobile tenant navigation in FundSubNavigation.
  if (isCapitalDomain()) {
    return null;
  }

  // The Event Companion is a standalone app with its own bottom nav — suppress
  // the main chiefintech.com mobile nav on its routes / subdomains.
  if (
    location.startsWith("/companion") ||
    (typeof window !== "undefined" &&
      (window.location.hostname === SIDE_EVENTS_HOST ||
        (/\.chiefinai\.com$/.test(window.location.hostname) &&
          !/^(www|events)\./.test(window.location.hostname))))
  ) {
    return null;
  }

  const activeSection = getActiveSection(location);

  const isActive = (href: string) => {
    if (href === "/") return location === "/";
    if (href === "/profile") return location === "/profile" || location === "/account" || location === "/home";
    if (href === "/media") return activeSection === "discover";
    if (href === "/directory") return activeSection === "network";
    if (href === "/capital") return activeSection === "capital";
    return false;
  };

  return (
    <nav
      className={cn(
        "fixed bottom-0 left-0 right-0 z-50 md:hidden",
        "bg-background/95 backdrop-blur border-t"
      )}
      aria-label="Mobile navigation"
      data-testid="mobile-bottom-nav"
    >
      <ul className="flex items-center justify-around h-16 px-2">
        {mobileBottomNavItems.map((item) => {
          const active = isActive(item.href);
          const itemSection = item.href === "/directory" || item.href === "/network" ? "network" : "";
          const crossDomain = itemSection ? needsCrossDomainNav(itemSection) : false;
          const linkClassName = cn(
            "flex flex-col items-center justify-center gap-1 min-w-[56px] min-h-[48px] rounded-lg transition-colors relative",
            active
              ? "text-primary"
              : "text-muted-foreground hover:text-foreground"
          );
          const linkContent = (
            <>
              <item.icon className={cn("h-5 w-5", active && "stroke-[2.5]")} />
              <span className={cn(
                "text-[10px] font-medium leading-none",
                active && "font-semibold"
              )}>{item.label}</span>
              {active && (
                <span className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-5 h-[3px] rounded-full bg-primary" />
              )}
            </>
          );
          return (
            <li key={item.href}>
              {crossDomain ? (
                <a
                  href={getCrossDomainHref(itemSection, item.href)}
                  className={linkClassName}
                  data-testid={`bottom-nav-${item.label.toLowerCase()}`}
                >
                  {linkContent}
                </a>
              ) : (
                <Link
                  href={item.href}
                  className={linkClassName}
                  data-testid={`bottom-nav-${item.label.toLowerCase()}`}
                >
                  {linkContent}
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
